import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import MasterSelect from './MasterSelect';
import WorkerSelect from './WorkerSelect';
import PriceModeFields from './PriceModeFields';

function fmt(n) {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 2 }).format(n || 0);
}
const qty = (n) => new Intl.NumberFormat('tr-TR').format(n || 0);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('tr-TR') : '');

const METHODS = [
  { value: 'direkt_ekim', label: '🌱 Direkt Ekim' },
  { value: 'fide', label: '🪴 Fide Dikimi' },
  { value: 'celik', label: '✂️ Çelik/Aşı' },
  { value: 'sogan_yumru', label: '🧅 Soğan/Yumru' },
];
const WORK_TYPES = ['Arazi Hazırlığı', 'Ekim', 'Dikim', 'Fide Dikimi', 'Budama', 'Sulama', 'Diğer'];
const EXPENSE_CHIPS = ['🚜 Traktör', '💧 Sulama', '🧪 İlaç', '🛢️ Mazot'];

const EMPTY_PLANTING = {
  seasonId: '', fieldId: '', masterProductId: '', productName: '', variety: '',
  plantingMethod: 'direkt_ekim', plantingDate: new Date().toISOString().split('T')[0],
  expectedHarvestDate: '', plantingAreaDecare: '', status: 'planted', notes: '',
  seedStockId: '', seedProductName: '', seedDisplayUnit: 'kg', seedingRateKgPerDecare: '', seedQuantityUsed: '',
};
const EMPTY_LABOUR = { workerName: '', farmWorkerId: '', workType: 'Ekim', numWorkers: 1, dailyRate: '', days: 1, paymentStatus: 'pending', workDate: new Date().toISOString().split('T')[0] };
const EMPTY_EXPENSE = { itemName: '', masterProductId: '', category: '', quantity: 1, unit: 'adet', unitPrice: '', totalAmount: '', priceMode: 'unit', paymentStatus: 'pending', reducesStock: false, stockId: '' };

export default function PlantingFlow({ onClose, onSaved, prefill }) {
  const [step, setStep] = useState(1);
  const [p, setP] = useState({ ...EMPTY_PLANTING, ...(prefill || {}) });
  const [labourList, setLabourList] = useState([]);
  const [expenseList, setExpenseList] = useState([]);
  const [fields, setFields] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [labourForm, setLabourForm] = useState(EMPTY_LABOUR);
  const [expenseForm, setExpenseForm] = useState(EMPTY_EXPENSE);
  const [expenseStock, setExpenseStock] = useState(null); // matched stock row for current expense product

  useEffect(() => {
    api.get('/farm/fields').then(r => setFields(r.data.data || [])).catch(() => {});
    api.get('/farm/stock').then(r => setStocks((r.data.data || []).filter(s => parseFloat(s.quantity) > 0))).catch(() => {});
    api.get('/seasons').then(r => {
      const data = r.data.data || [];
      setSeasons(data);
      const active = data.find(s => s.is_active);
      setP(prev => (active && !prev.seasonId ? { ...prev, seasonId: active.id } : prev));
    }).catch(() => {});
  }, []); // eslint-disable-line

  const set = (k, v) => setP(prev => ({ ...prev, [k]: v }));

  const selectField = (fieldId) => {
    set('fieldId', fieldId);
    const field = fields.find(f => f.id === fieldId);
    if (field && field.size_decare && !p.plantingAreaDecare) set('plantingAreaDecare', field.size_decare);
  };

  const selectSeed = (stockId) => {
    const s = stocks.find(i => i.id === stockId);
    setP(prev => ({ ...prev, seedStockId: stockId, seedProductName: s ? s.product_name : '', seedDisplayUnit: s ? (s.display_unit || s.unit || 'kg') : 'kg' }));
  };

  // ─── Labour ──────────────────────────────────────────────────────────
  const addLabour = () => {
    if (!labourForm.workerName) return;
    const total = (parseFloat(labourForm.dailyRate) || 0) * (parseFloat(labourForm.days) || 1) * (parseInt(labourForm.numWorkers) || 1);
    setLabourList(prev => [...prev, { ...labourForm, totalAmount: total }]);
    setLabourForm(EMPTY_LABOUR);
  };

  // ─── Expense (with flexible pricing) ─────────────────────────────────
  const setExp = (k, v) => setExpenseForm(f => {
    const next = { ...f, [k]: v };
    if (!['quantity', 'unitPrice', 'totalAmount', 'priceMode'].includes(k)) return next;
    const q = parseFloat(next.quantity);
    if (next.priceMode === 'total') {
      const total = parseFloat(next.totalAmount);
      next.unitPrice = (q > 0 && !isNaN(total)) ? String(total / q) : '';
    } else {
      const up = parseFloat(next.unitPrice);
      next.totalAmount = (q > 0 && !isNaN(up)) ? String(q * up) : '';
    }
    return next;
  });

  const onExpenseProduct = (item) => {
    const stock = stocks.find(s => s.product_name === item.name);
    setExpenseStock(stock || null);
    setExpenseForm(f => ({ ...f, masterProductId: item.id, category: item.category || '', unit: item.unit || f.unit, stockId: stock ? stock.id : '' }));
  };

  const addExpense = () => {
    if (!expenseForm.itemName) return;
    const q = parseFloat(expenseForm.quantity) || 0;
    const up = parseFloat(expenseForm.unitPrice) || 0;
    const total = parseFloat(expenseForm.totalAmount) || q * up;
    setExpenseList(prev => [...prev, { ...expenseForm, unitPrice: up, totalAmount: total, stockName: expenseStock ? expenseStock.product_name : null }]);
    setExpenseForm(EMPTY_EXPENSE);
    setExpenseStock(null);
  };

  // ─── Derived ─────────────────────────────────────────────────────────
  const seedStock = stocks.find(s => s.id === p.seedStockId);
  const seedAvailable = seedStock ? parseFloat(seedStock.quantity) : 0;
  const calcSeedQty = () => (parseFloat(p.seedingRateKgPerDecare) || 0) * (parseFloat(p.plantingAreaDecare) || 0);
  const seedQtyToUse = parseFloat(p.seedQuantityUsed) || calcSeedQty();
  const seedCost = seedStock ? seedQtyToUse * (parseFloat(seedStock.avg_cost_per_unit) || 0) : 0;
  const totalLabour = labourList.reduce((s, l) => s + (l.totalAmount || 0), 0);
  const totalExpense = expenseList.reduce((s, e) => s + (e.totalAmount || 0), 0);
  const totalCost = seedCost + totalLabour + totalExpense;
  const fieldName = (fid) => { const f = fields.find(x => x.id === fid); return f ? f.name : ''; };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post('/plantings/unified', {
        planting: { ...p, seedQuantityUsed: seedQtyToUse || null, seedDisplayQuantity: seedQtyToUse || null },
        labour: labourList,
        expenses: expenseList,
      });
      toast.success('✅ Ekim kaydedildi');
      if (onSaved) onSaved(r.data.plantingId);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.error || 'Kayıt başarısız');
    } finally { setSaving(false); }
  };

  const dots = [1, 2, 3, 4, 5].map(i => (
    <span key={i} style={{ width: 10, height: 10, borderRadius: '50%', background: step >= i ? 'var(--green-mid)' : '#ddd' }} />
  ));
  const primary = { width: '100%', justifyContent: 'center' };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-sheet" onClick={e => e.stopPropagation()} style={{ maxHeight: '92vh', overflow: 'auto' }}>
        <div className="modal-handle" />
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 12 }}>{dots}</div>

        {/* STEP 1 — Ekim Bilgileri */}
        {step === 1 && (
          <div>
            <div className="modal-title">Yeni Ekim (1/5)</div>
            <div className="form-group">
              <label>Sezon</label>
              <select value={p.seasonId} onChange={e => set('seasonId', e.target.value)}>
                <option value="">Seçin</option>
                {seasons.map(s => <option key={s.id} value={s.id}>{s.name}{s.is_active ? ' ●' : ''}</option>)}
              </select>
              {seasons.length === 0 && <a href="/settings" style={{ fontSize: '0.75rem', color: 'var(--green-mid)' }}>⚙️ Sezon ekle</a>}
            </div>
            <div className="form-group">
              <label>Tarla *</label>
              <select value={p.fieldId} onChange={e => selectField(e.target.value)} required>
                <option value="">Seçin</option>
                {fields.map(f => <option key={f.id} value={f.id}>{f.size_decare ? `${f.name} (${f.size_decare} da)` : f.name}</option>)}
              </select>
              {fields.length === 0 && <a href="/fields" style={{ fontSize: '0.75rem', color: 'var(--green-mid)' }}>⚙️ Tarla ekle</a>}
            </div>
            <MasterSelect type="product" usageType="sell" priorityCategory="crop" farmTypeFilter
              label="Ürün *" value={p.productName} onChange={v => set('productName', v)}
              onSelect={item => { set('masterProductId', item.id); if (item.variety) set('variety', item.variety); }}
              placeholder="Buğday, Kiraz, Domates..." required />
            <div className="form-group">
              <label>Çeşit (isteğe bağlı)</label>
              <input value={p.variety} onChange={e => set('variety', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Yöntem</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {METHODS.map(m => (
                  <button key={m.value} type="button" onClick={() => set('plantingMethod', m.value)}
                    style={{ padding: '10px 6px', borderRadius: 8, border: p.plantingMethod === m.value ? '2px solid var(--green-mid)' : '1px solid var(--border)',
                      background: p.plantingMethod === m.value ? 'var(--green-pale)' : '#fff', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer' }}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Ekim Tarihi</label>
                <input type="date" value={p.plantingDate} onChange={e => set('plantingDate', e.target.value)} /></div>
              <div className="form-group"><label>Tahmini Hasat</label>
                <input type="date" value={p.expectedHarvestDate} onChange={e => set('expectedHarvestDate', e.target.value)} /></div>
            </div>
            <div className="form-group">
              <label>Ekim Alanı (dekar)</label>
              <input type="number" step="0.1" value={p.plantingAreaDecare} onChange={e => set('plantingAreaDecare', e.target.value)} placeholder="Tarlada ekilecek alan" />
            </div>
            <div className="form-group">
              <label>Durum</label>
              <div style={{ display: 'flex', gap: 6 }}>
                {[['planned', '📋 Planlandı'], ['planted', '✅ Ekildi/Dikildi']].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => set('status', v)}
                    style={{ flex: 1, padding: '8px', borderRadius: 8, border: p.status === v ? '2px solid var(--green-mid)' : '1px solid var(--border)',
                      background: p.status === v ? 'var(--green-pale)' : '#fff', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer' }}>{l}</button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label>Notlar (isteğe bağlı)</label>
              <textarea value={p.notes} onChange={e => set('notes', e.target.value)} rows={2} />
            </div>
            <button className="btn btn-primary" style={primary} onClick={() => setStep(2)} disabled={!p.productName || !p.fieldId}>Devam →</button>
          </div>
        )}

        {/* STEP 2 — Tohum */}
        {step === 2 && (
          <div>
            <div className="modal-title">Tohum & Materyal (2/5)</div>
            <p style={{ fontSize: '0.82rem', color: '#888', marginBottom: 8 }}>Hangi tohum veya fidanı kullandınız?</p>
            <div className="form-group">
              <label>Tohum (stoktan)</label>
              <select value={p.seedStockId} onChange={e => selectSeed(e.target.value)}>
                <option value="">Tohum yok / Atla</option>
                {stocks.map(s => <option key={s.id} value={s.id}>{s.product_name} ({qty(s.quantity)} {s.display_unit || s.unit})</option>)}
              </select>
            </div>
            {p.seedStockId && (
              <>
                <div style={{ background: seedAvailable > 0 ? '#f0f7ee' : '#fdecea', borderRadius: 8, padding: '6px 10px', marginBottom: 8, fontSize: '0.82rem' }}>
                  {seedAvailable > 0 ? <>📦 Mevcut stok: <b>{qty(seedAvailable)} {p.seedDisplayUnit}</b></> : <>⚠️ Bu üründen stoğunuz yok</>}
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Ekim Normu (kg/da)</label>
                    <input type="number" step="0.1" value={p.seedingRateKgPerDecare} onChange={e => set('seedingRateKgPerDecare', e.target.value)} placeholder="1 dekara ne kadar?" /></div>
                  <div className="form-group"><label>Kullanılan Miktar</label>
                    <input type="number" step="0.1" value={p.seedQuantityUsed || (calcSeedQty() > 0 ? calcSeedQty().toFixed(1) : '')}
                      onChange={e => set('seedQuantityUsed', e.target.value)} placeholder={calcSeedQty() > 0 ? calcSeedQty().toFixed(1) : '0'} /></div>
                </div>
                {calcSeedQty() > 0 && (
                  <div style={{ fontSize: '0.76rem', color: '#888', marginBottom: 6 }}>
                    Toplam ihtiyaç: {calcSeedQty().toFixed(1)} {p.seedDisplayUnit} ({qty(p.plantingAreaDecare)} da × {p.seedingRateKgPerDecare} kg/da)
                  </div>
                )}
                {seedQtyToUse > seedAvailable && <div style={{ color: '#e74c3c', fontSize: '0.78rem', fontWeight: 700, marginBottom: 4 }}>⚠️ Tohum stoğu yetersiz</div>}
                <div style={{ fontSize: '0.85rem', color: '#8b5e3c', fontWeight: 700, marginBottom: 8 }}>Tahmini tohum maliyeti: {fmt(seedCost)}</div>
              </>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }} onClick={() => setStep(1)}>← Geri</button>
              <button className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }} onClick={() => setStep(3)}>Devam →</button>
            </div>
          </div>
        )}

        {/* STEP 3 — İşçilik */}
        {step === 3 && (
          <div>
            <div className="modal-title">İşçilik (3/5)</div>
            <p style={{ fontSize: '0.82rem', color: '#888', marginBottom: 8 }}>Ekim için kaç kişi çalıştı?</p>
            {labourList.map((l, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: '#f9f9f9', borderRadius: 8, marginBottom: 4, fontSize: '0.82rem' }}>
                <span>{l.workerName} · {l.workType} · {l.numWorkers}×{l.days} gün</span>
                <div><span style={{ fontWeight: 700 }}>{fmt(l.totalAmount)}</span>
                  <button onClick={() => setLabourList(prev => prev.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: '#c00', cursor: 'pointer', marginLeft: 4 }}>✕</button>
                </div>
              </div>
            ))}
            <WorkerSelect label="İşçi / Grup Adı" value={labourForm.workerName}
              onChange={v => setLabourForm(f => ({ ...f, workerName: v }))}
              onWorkerSelect={w => setLabourForm(f => ({ ...f, workerName: `${w.first_name} ${w.last_name}`, farmWorkerId: w.id, dailyRate: w.default_daily_rate || f.dailyRate }))}
              placeholder="Ahmet, Sezonluk Ekip..." />
            <div className="form-row">
              <div className="form-group"><label>İş Türü</label>
                <select value={labourForm.workType} onChange={e => setLabourForm(f => ({ ...f, workType: e.target.value }))}>
                  {WORK_TYPES.map(w => <option key={w} value={w}>{w}</option>)}
                </select></div>
              <div className="form-group"><label>Kişi</label><input type="number" min="1" value={labourForm.numWorkers} onChange={e => setLabourForm(f => ({ ...f, numWorkers: e.target.value }))} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Gün</label><input type="number" step="0.5" value={labourForm.days} onChange={e => setLabourForm(f => ({ ...f, days: e.target.value }))} /></div>
              <div className="form-group"><label>Günlük (₺)</label><input type="number" value={labourForm.dailyRate} onChange={e => setLabourForm(f => ({ ...f, dailyRate: e.target.value }))} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Tarih</label><input type="date" value={labourForm.workDate} onChange={e => setLabourForm(f => ({ ...f, workDate: e.target.value }))} /></div>
              <div className="form-group"><label>Ödeme</label>
                <select value={labourForm.paymentStatus} onChange={e => setLabourForm(f => ({ ...f, paymentStatus: e.target.value }))}>
                  <option value="paid">Ödendi</option><option value="pending">Bekliyor</option>
                </select></div>
            </div>
            <button type="button" onClick={addLabour} style={{ width: '100%', padding: '8px', background: 'var(--green-pale)', border: 'none', borderRadius: 8, fontSize: '0.82rem', fontWeight: 700, color: 'var(--green-mid)', cursor: 'pointer', marginBottom: 8 }}>+ Başka İşçi/Grup Ekle</button>
            {totalLabour > 0 && <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 8 }}>Toplam İşçilik: {fmt(totalLabour)}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }} onClick={() => setStep(2)}>← Geri</button>
              <button className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }} onClick={() => setStep(4)}>Devam →</button>
            </div>
          </div>
        )}

        {/* STEP 4 — Giderler */}
        {step === 4 && (
          <div>
            <div className="modal-title">Diğer Giderler (4/5)</div>
            <p style={{ fontSize: '0.82rem', color: '#888', marginBottom: 8 }}>Makine, ilaç, naylon ve diğer harcamalar</p>
            <div className="chip-group" style={{ marginBottom: 8 }}>
              {EXPENSE_CHIPS.map(c => (
                <button key={c} className="chip" onClick={() => setExpenseForm(f => ({ ...f, itemName: c.replace(/^\S+\s/, '') }))}>{c}</button>
              ))}
            </div>
            {expenseList.map((e, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: '#f9f9f9', borderRadius: 8, marginBottom: 4, fontSize: '0.82rem' }}>
                <span>{e.itemName} · {e.quantity} {e.unit}{e.reducesStock ? ' · 📦' : ''}</span>
                <div><span style={{ fontWeight: 700 }}>{fmt(e.totalAmount)}</span>
                  <button onClick={() => setExpenseList(prev => prev.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: '#c00', cursor: 'pointer', marginLeft: 4 }}>✕</button>
                </div>
              </div>
            ))}
            <MasterSelect type="product" usageType="buy" label="Gider / Ürün"
              value={expenseForm.itemName} onChange={v => setExpenseForm(f => ({ ...f, itemName: v }))}
              onSelect={onExpenseProduct} placeholder="Traktör, İlaç, Naylon..." />
            {expenseStock && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f0f7ee', borderRadius: 8, padding: '6px 10px', marginBottom: 8, fontSize: '0.8rem' }}>
                <span>📦 Stok: {qty(expenseStock.quantity)} {expenseStock.display_unit || expenseStock.unit}</span>
                <label style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input type="checkbox" checked={expenseForm.reducesStock} onChange={e => setExpenseForm(f => ({ ...f, reducesStock: e.target.checked }))} />
                  Stoktan düş
                </label>
              </div>
            )}
            <div className="form-row">
              <div className="form-group"><label>Miktar</label><input type="number" step="0.01" value={expenseForm.quantity} onChange={e => setExp('quantity', e.target.value)} /></div>
              <div className="form-group"><label>Birim</label><input value={expenseForm.unit} onChange={e => setExpenseForm(f => ({ ...f, unit: e.target.value }))} /></div>
            </div>
            <PriceModeFields mode={expenseForm.priceMode} unit={expenseForm.unit}
              quantity={expenseForm.quantity} unitPrice={expenseForm.unitPrice} totalAmount={expenseForm.totalAmount}
              onMode={m => setExp('priceMode', m)} onUnitPrice={v => setExp('unitPrice', v)} onTotal={v => setExp('totalAmount', v)} />
            <div className="form-group"><label>Ödeme</label>
              <select value={expenseForm.paymentStatus} onChange={e => setExpenseForm(f => ({ ...f, paymentStatus: e.target.value }))}>
                <option value="paid">Ödendi</option><option value="pending">Bekliyor</option>
              </select></div>
            <button type="button" onClick={addExpense} style={{ width: '100%', padding: '8px', background: 'var(--green-pale)', border: 'none', borderRadius: 8, fontSize: '0.82rem', fontWeight: 700, color: 'var(--green-mid)', cursor: 'pointer', marginBottom: 8 }}>+ Başka Gider Ekle</button>
            {totalExpense > 0 && <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 8 }}>Toplam Gider: {fmt(totalExpense)}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }} onClick={() => setStep(3)}>← Geri</button>
              <button className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }} onClick={() => setStep(5)}>Özete Git →</button>
            </div>
          </div>
        )}

        {/* STEP 5 — Özet */}
        {step === 5 && (
          <div>
            <div className="modal-title">Özet (5/5)</div>
            <div className="card" style={{ padding: 12, marginBottom: 8, background: '#f0f7ee' }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>🌱 Ekim</div>
              <div style={{ fontSize: '0.82rem', lineHeight: 1.5 }}>
                {p.productName} {p.variety && `(${p.variety})`} · {fieldName(p.fieldId)}{p.plantingAreaDecare && ` · ${p.plantingAreaDecare} da`}<br />
                {fmtDate(p.plantingDate)}{p.expectedHarvestDate && ` → Tahmini hasat: ${fmtDate(p.expectedHarvestDate)}`}<br />
                Yöntem: {(METHODS.find(m => m.value === p.plantingMethod) || {}).label} · Durum: {p.status === 'planned' ? 'Planlandı' : 'Ekildi / Dikildi'}
              </div>
            </div>
            <div className="card" style={{ padding: 12, marginBottom: 8 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>🌰 Tohum</div>
              <div style={{ fontSize: '0.82rem' }}>
                {p.seedStockId ? <>{p.seedProductName} · {seedQtyToUse.toFixed(1)} {p.seedDisplayUnit} · Tahmini: {fmt(seedCost)}</> : 'Tohum eklenmedi'}
              </div>
            </div>
            <div className="card" style={{ padding: 12, marginBottom: 8 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>👷 İşçilik ({labourList.length} giriş)</div>
              {labourList.length === 0 ? <div style={{ fontSize: '0.82rem', color: '#999' }}>İşçilik eklenmedi</div>
                : <>{labourList.map((l, i) => <div key={i} style={{ fontSize: '0.82rem' }}>{l.workerName}: {l.numWorkers} kişi × {l.days} gün = {fmt(l.totalAmount)}</div>)}
                  <div style={{ fontWeight: 700, marginTop: 4 }}>Toplam işçilik: {fmt(totalLabour)}</div></>}
            </div>
            <div className="card" style={{ padding: 12, marginBottom: 8 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>🛒 Giderler ({expenseList.length} kalem)</div>
              {expenseList.length === 0 ? <div style={{ fontSize: '0.82rem', color: '#999' }}>Gider eklenmedi</div>
                : <>{expenseList.map((e, i) => <div key={i} style={{ fontSize: '0.82rem' }}>{e.itemName}: {e.quantity} {e.unit} = {fmt(e.totalAmount)}</div>)}
                  <div style={{ fontWeight: 700, marginTop: 4 }}>Toplam gider: {fmt(totalExpense)}</div></>}
            </div>
            <div className="card" style={{ padding: 12, marginBottom: 12, background: '#e8f5e4' }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>📊 Toplam Maliyet</div>
              <div style={{ fontSize: '0.85rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Tohum:</span><span>{fmt(seedCost)}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>İşçilik:</span><span>{fmt(totalLabour)}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Giderler:</span><span>{fmt(totalExpense)}</span></div>
                <div style={{ borderTop: '1px solid #cbe3c0', marginTop: 4, paddingTop: 4, display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: '1.1rem', color: '#1c3a1c' }}>
                  <span>Toplam:</span><span>{fmt(totalCost)}</span>
                </div>
              </div>
            </div>
            {(p.seedStockId || expenseList.some(e => e.reducesStock)) && (
              <div style={{ fontSize: '0.78rem', color: '#8a6d00', background: '#fff8e1', borderRadius: 8, padding: '8px 10px', marginBottom: 12 }}>
                {p.seedStockId && <div>Stoktan düşülecek: {p.seedProductName} {seedQtyToUse.toFixed(1)} {p.seedDisplayUnit}{seedQtyToUse > seedAvailable ? ` ⚠️ (${qty(seedAvailable)} var)` : ''}</div>}
                {expenseList.filter(e => e.reducesStock).map((e, i) => <div key={i}>Stoktan düşülecek: {e.itemName} {e.quantity} {e.unit}</div>)}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }} onClick={() => setStep(4)}>← Geri</button>
              <button className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }} onClick={save} disabled={saving}>
                {saving ? 'Kaydediliyor...' : '✅ Ekimi Kaydet'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
